import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Autocomplete,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { NotificationManager } from "react-notifications";
import ScannerNew from "../../../../components/common/ScannerNew";
import {
  GetInventoryLocations,
  LookupInventoryCode,
  PostInventoryMovement,
  GetErrorMessage,
} from "../../../services/InventoryTrackerService";
import { GetProductById } from "../../../services/InventoryService";
import { GetMultiSelectClients } from "../../../services/ClientsMaintenanceService";
import { PrintInventoryLabel } from "../../../../services/barcodeService";
import CommonLoader from "../../common/CommonLoader";

const OTHER_CLIENT = { accountNumber: "", clientName: "Other" };

const TITLES = {
  Receive: "Receive Into Stock",
  Use: "Use From Stock",
  Move: "Move Stock",
  Count: "Physical Count",
};

// Dates come back as "2027-06-30T00:00:00" with no zone; parsing them as a Date shifts them a day
// in time zones ahead of UTC, so the calendar date is read straight from the text.
export const formatDate = (value) => {
  if (!value) return "";
  const [year, month, day] = String(value).split("T")[0].split("-");
  return year && month && day ? `${Number(month)}/${Number(day)}/${year}` : "";
};

/**
 * ENB-584: one dialog for the four stock actions.
 * - Receive: product (+ variant) into a location, with optional lot / expiry / PO.
 * - Use / Move / Count: against an existing stock row (product at a location, per lot).
 * Locations can be picked from the list or scanned from the shelf/rack label.
 */
const StockActionDialog = ({ open, action, product, stockRow, onClose, onDone }) => {
  const [locations, setLocations] = useState([]);
  const [variants, setVariants] = useState([]);
  const [clients, setClients] = useState([]);
  const [scanningLocation, setScanningLocation] = useState(false);
  const [saving, setSaving] = useState(false);

  const [variantId, setVariantId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [lotNumber, setLotNumber] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [client, setClient] = useState(null);
  const [otherText, setOtherText] = useState("");
  const [notes, setNotes] = useState("");
  const [printLabel, setPrintLabel] = useState(false);

  useEffect(() => {
    if (!open) return;

    setVariantId(product?.productVariantId ?? "");
    setLocationId("");
    // Move defaults to the whole row; Count starts at what the system has so only differences are typed.
    setQuantity(action === "Move" || action === "Count" ? stockRow?.stockLevel ?? "" : "");
    setLotNumber("");
    setExpirationDate("");
    setPoNumber("");
    setClient(null);
    setOtherText("");
    setNotes("");
    setPrintLabel(false);
    setScanningLocation(false);

    if (action === "Receive" || action === "Move") loadLocations();
    if (action === "Receive" && product?.productId && !product?.productVariantId) loadVariants();
    if (action === "Use" && clients.length === 0) loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action]);

  const loadLocations = async () => {
    try {
      const response = await GetInventoryLocations();
      setLocations(response?.results || []);
    } catch {
      NotificationManager.error("Error loading locations");
    }
  };

  const loadVariants = async () => {
    try {
      const response = await GetProductById(product.productId);
      setVariants((response?.results?.variants || []).filter((v) => v.isActive));
    } catch {
      setVariants([]);
    }
  };

  const loadClients = async () => {
    try {
      const response = await GetMultiSelectClients("");
      setClients(
        (response?.results || []).map((c) => ({
          accountNumber: c.accountNumber.trim(),
          clientName: c.clientName.trim(),
        }))
      );
    } catch {
      NotificationManager.error("Error loading clients");
    }
  };

  const handleLocationScan = async (code) => {
    setScanningLocation(false);
    try {
      const response = await LookupInventoryCode(code);
      const result = response?.results;
      if (result?.matchType === "Location" && result.location) {
        setLocationId(result.location.id);
        if (!locations.some((l) => l.id === result.location.id)) {
          setLocations([...locations, result.location]);
        }
      } else {
        NotificationManager.warning(`"${code}" is not a location barcode`);
      }
    } catch {
      NotificationManager.error("Error looking up the location");
    }
  };

  const validate = () => {
    const qty = Number(quantity);
    if (quantity === "" || Number.isNaN(qty)) return "Enter a quantity";
    if (action === "Count" ? qty < 0 : qty <= 0) return "Quantity must be more than zero";
    if ((action === "Use" || action === "Move") && qty > stockRow.stockLevel)
      return `Only ${stockRow.stockLevel} on hand here`;
    if ((action === "Receive" || action === "Move") && !locationId) return "Pick a location";
    if (action === "Use" && !client) return "Pick the client, or Other";
    if (action === "Use" && client === OTHER_CLIENT && !otherText.trim())
      return "Enter what the stock is used for";
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      NotificationManager.error(error);
      return;
    }

    const payload = {
      transactionType: action,
      productId: product?.productId ?? stockRow?.productId,
      productVariantId:
        action === "Receive" ? (variantId === "" ? null : Number(variantId)) : stockRow?.productVariantId ?? null,
      productLocationId: stockRow?.id ?? null,
      locationId: locationId ? Number(locationId) : null,
      lotNumber: lotNumber.trim() || null,
      expirationDate: expirationDate || null,
      poNumber: poNumber.trim() || null,
      quantity: Number(quantity),
      clientId: client && client !== OTHER_CLIENT ? client.accountNumber : null,
      usedFor: client === OTHER_CLIENT ? otherText.trim() : null,
      notes: notes.trim() || null,
    };

    setSaving(true);
    try {
      await PostInventoryMovement(payload);
      NotificationManager.success(`${TITLES[action]} saved`);

      if (action === "Receive" && printLabel) {
        await printReceivedLabel(payload);
      }

      onDone();
    } catch (err) {
      NotificationManager.error(await GetErrorMessage(err, "Error saving"));
    } finally {
      setSaving(false);
    }
  };

  const printReceivedLabel = async (payload) => {
    const variant = variants.find((v) => v.id === payload.productVariantId);
    try {
      await PrintInventoryLabel("inventoryLabel", {
        "{{name}}": variant ? `${product.productName} - ${variant.variantName}` : product.productName,
        "{{barcode}}": variant?.sku || product.itemNumber || "",
        "{{expiration}}": payload.expirationDate ? formatDate(payload.expirationDate) : "-",
        "{{quantity}}": payload.quantity,
        "{{lot}}": payload.lotNumber || "-",
      });
    } catch (err) {
      NotificationManager.error(err?.message || "Stock saved, but the label did not print");
    }
  };

  const productLabel = () => {
    const name = product?.productName ?? stockRow?.productName;
    const variant = product?.variantName ?? stockRow?.variantName;
    return variant ? `${name} - ${variant}` : name;
  };

  const locationPicker = (label) => (
    <Grid item xs={12} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <TextField
        size="small"
        select
        fullWidth
        label={label}
        value={locationId}
        onChange={(e) => setLocationId(e.target.value)}
      >
        {locations
          .filter((l) => l.id !== stockRow?.locationId)
          .map((l) => (
            <MenuItem key={l.id} value={l.id}>
              {l.fullPath}
            </MenuItem>
          ))}
      </TextField>
      <Tooltip title="Scan the shelf or rack label">
        <IconButton color="primary" onClick={() => setScanningLocation(!scanningLocation)}>
          <QrCodeScannerIcon />
        </IconButton>
      </Tooltip>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{TITLES[action]}</DialogTitle>
      <DialogContent>
        <Grid container spacing={1.5} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle2">{productLabel()}</Typography>
            {stockRow && (
              <Typography variant="body2" color="text.secondary">
                {stockRow.locationName}
                {stockRow.lotNumber ? ` | Lot ${stockRow.lotNumber}` : ""}
                {stockRow.expirationDate ? ` | Exp ${formatDate(stockRow.expirationDate)}` : ""}
                {` | On hand: ${stockRow.stockLevel}`}
              </Typography>
            )}
          </Grid>

          {action === "Receive" && variants.length > 0 && (
            <Grid item xs={12}>
              <TextField
                size="small"
                select
                fullWidth
                label="Variant"
                value={variantId}
                onChange={(e) => setVariantId(e.target.value)}
              >
                <MenuItem value="">(product)</MenuItem>
                {variants.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.variantName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {action === "Receive" && locationPicker("Receive Into *")}
          {action === "Move" && locationPicker("Move To *")}

          {scanningLocation && (
            <Grid item xs={12}>
              <ScannerNew onScan={handleLocationScan} />
            </Grid>
          )}

          <Grid item xs={action === "Receive" ? 4 : 12}>
            <TextField
              size="small"
              fullWidth
              type="number"
              label={action === "Count" ? "Counted Quantity *" : "Quantity (units) *"}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              autoFocus
            />
          </Grid>

          {action === "Receive" && (
            <>
              <Grid item xs={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Lot Number"
                  value={lotNumber}
                  onChange={(e) => setLotNumber(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  size="small"
                  fullWidth
                  type="date"
                  label="Expiration"
                  InputLabelProps={{ shrink: true }}
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="PO #"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox size="small" checked={printLabel} onChange={(e) => setPrintLabel(e.target.checked)} />
                  }
                  label="Print label"
                />
              </Grid>
            </>
          )}

          {action === "Use" && (
            <>
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  options={[OTHER_CLIENT, ...clients]}
                  value={client}
                  onChange={(e, value) => setClient(value)}
                  getOptionLabel={(option) =>
                    option === OTHER_CLIENT ? "Other" : `${option.clientName} (${option.accountNumber})`
                  }
                  isOptionEqualToValue={(option, value) => option.accountNumber === value.accountNumber}
                  renderInput={(params) => <TextField {...params} label="Used For (client) *" />}
                />
              </Grid>
              {client === OTHER_CLIENT && (
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Used For *"
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value)}
                    inputProps={{ maxLength: 200 }}
                  />
                </Grid>
              )}
            </>
          )}

          {action === "Count" && stockRow && quantity !== "" && Number(quantity) !== stockRow.stockLevel && (
            <Grid item xs={12}>
              <Typography variant="body2" color="error">
                Variance: {Number(quantity) - stockRow.stockLevel > 0 ? "+" : ""}
                {Number(quantity) - stockRow.stockLevel}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              inputProps={{ maxLength: 400 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
      <CommonLoader loading={saving} />
    </Dialog>
  );
};

export default StockActionDialog;
